import { useEffect } from 'react';
import { DataStatus } from '@/shared/lib';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import {
  fetchCreatives,
  fetchCreativesByAdName,
  initCreativesAuth,
  selectCreativeByAdId,
  selectCreativeByAdName,
  selectCreativesAuthStatus,
} from './slice';
import type { CreativeItem } from './types';

let pendingIds = new Set<string>();
let scheduled = false;

export function useRequestCreative(adId: string): CreativeItem | undefined {
  const dispatch = useAppDispatch();
  const creative = useAppSelector(selectCreativeByAdId(adId));
  const authStatus = useAppSelector(selectCreativesAuthStatus);

  useEffect(() => {
    if (creative || !adId) return;

    pendingIds.add(adId);

    if (!scheduled) {
      scheduled = true;
      Promise.resolve().then(() => {
        const ids = Array.from(pendingIds);
        pendingIds.clear();
        scheduled = false;

        if (authStatus === DataStatus.idle) {
          Promise.all([
            dispatch(initCreativesAuth()),
            dispatch(fetchCreatives(ids)),
          ]);
        } else {
          dispatch(fetchCreatives(ids));
        }
      });
    }
  }, [adId, creative, authStatus, dispatch]);

  return creative;
}

let pendingAdNames = new Set<string>();
let scheduledAdName = false;

export function useRequestCreativeByAdName(
  adName: string,
): CreativeItem | undefined {
  const dispatch = useAppDispatch();
  const creative = useAppSelector(selectCreativeByAdName(adName));
  const authStatus = useAppSelector(selectCreativesAuthStatus);

  useEffect(() => {
    if (creative || !adName) return;

    pendingAdNames.add(adName);

    if (!scheduledAdName) {
      scheduledAdName = true;
      Promise.resolve().then(() => {
        const names = Array.from(pendingAdNames);
        pendingAdNames.clear();
        scheduledAdName = false;

        if (authStatus === DataStatus.idle) {
          Promise.all([
            dispatch(initCreativesAuth()),
            dispatch(fetchCreativesByAdName(names)),
          ]);
        } else {
          dispatch(fetchCreativesByAdName(names));
        }
      });
    }
  }, [adName, creative, authStatus, dispatch]);

  return creative;
}

export function useRequestCreativeForDimension(
  value: string,
  dimension: string,
): CreativeItem | undefined {
  const byAdName = dimension === 'ad_name';
  const creativeByAdId = useRequestCreative(byAdName ? '' : value);
  const creativeByAdName = useRequestCreativeByAdName(byAdName ? value : '');
  return byAdName ? creativeByAdName : creativeByAdId;
}
